import { ModuleTree } from "vuex";

import { FalconService } from "@/services/falcon.service";

import { GetMyComplaintsFalconRequest } from "@/interfaces/falcon/get-my-complaints/get-my-complaints-falcon-request";
import { GetMyComplaintsFalconResponse } from "@/interfaces/falcon/get-my-complaints/get-my-complaints-falcon-response";
import { GetComplaintQuantityAvailableFalconRequest } from "@/interfaces/falcon/get-complaint-quantity-available/get-complaint-quantity-available-falcon-request";
import { GetComplaintQuantityAvailableFalconResponse } from "@/interfaces/falcon/get-complaint-quantity-available/get-complaint-quantity-available-falcon-response";
import { DoCreateComplaintFalconRequest } from "@/interfaces/falcon/do-create-complaint/do-create-complaint-falcon-request";
import { DoCreateComplaintFalconResponse } from "@/interfaces/falcon/do-create-complaint/do-create-complaint-falcon-response";
import { GetMyComplaintsComplaintListItem } from "@/interfaces/falcon/get-my-complaints/get-my-complaints-complaint-list-item";
import { DoCreateComplaintFirstStepFormData } from "@/interfaces/falcon/do-create-complaint/do-create-complaint-first-step-form-data";
import { DoCreateComplaintSecondStepFormData } from "@/interfaces/falcon/do-create-complaint/do-create-complaint-second-step-form-data";

import { RequestAlias } from "@/enums/request-alias.enum";
import { FalconComplaintPositiveDecision } from "@/enums/falcon/complaints/falcon-complaint-positive-decision.enum";

/**
 * @author Damian Kajzer <damian.kajzer@sowlgroup.com>
 * Falcon complaints vuex module
 */
export const FalconComplaintsModule: ModuleTree<unknown> = {
  falconComplaints: {
    namespaced: true,
    state: {
      complaintsData: [],
      complaintsTotalCount: 0,
      complaintQuantityAvailable: 0,
      createdComplaintId: undefined,
      complaintFormData: {
        details: {
          dateProductDelivery: undefined,
          serialNumber: undefined,
          quantity: 1,
          decisionIfYes: FalconComplaintPositiveDecision.Exchange,
          decisionIfNo: undefined,
          faultFoundBy: undefined,
          faultWhen: undefined,
          refuseAddCosts: undefined,
          compensationValue: undefined,
          compensationCurrency: undefined
        } as DoCreateComplaintFirstStepFormData,
        vehicle: {
          registerNumber: undefined,
          engineNumber: undefined,
          vinNumber: undefined,
          brandVehicle: undefined,
          productionYear: undefined,
          engineCapacity: undefined,
          powerValue: undefined,
          powerUnit: undefined,
          installDate: undefined,
          counterStateInstall: 0,
          installedBy: undefined,
          workshopInstallName: undefined,
          workshopInstallAddress: undefined,
          uninstallDate: undefined,
          counterStateUninstall: 0,
          uninstalledBy: undefined,
          workshopUninstallName: undefined,
          workshopUninstallAddress: undefined
        } as DoCreateComplaintSecondStepFormData,
        defectDescription: undefined
      }
    },
    actions: {
      /**
       * Fetches complaints
       * @param commit Vuex commit
       * @param request Request data
       */
      getComplaints({ commit }, request: GetMyComplaintsFalconRequest) {
        FalconService.request<
          GetMyComplaintsFalconRequest,
          GetMyComplaintsFalconResponse
        >(RequestAlias.GetMyComplaints, request).then(response => {
          if (
            response.data.getMyComplaintsResponse.complaintList?.complaint
              ?.length
          ) {
            const {
              complaintList,
              complaintsCount
            } = response.data.getMyComplaintsResponse;
            commit("complaintsReceived", complaintList.complaint);
            commit("complaintsTotalCountReceived", +complaintsCount);
          } else {
            commit("complaintsReceived", []);
            commit("complaintsTotalCountReceived", 0);
          }
        });
      },

      /**
       * Fetches available quantity of product
       * @param commit Vuex commit
       * @param request Request data
       */
      getComplaintQuantityAvailable(
        { commit },
        request: GetComplaintQuantityAvailableFalconRequest
      ) {
        FalconService.request<
          GetComplaintQuantityAvailableFalconRequest,
          GetComplaintQuantityAvailableFalconResponse
        >(RequestAlias.GetComplaintQuantityAvailable, request).then(
          response => {
            commit(
              "complaintAvailableQuantityReceived",
              +response.data.getComplaintQuantityAvailableResponse.quantity
            );
          }
        );
      },

      /**
       * Makes a complaint creation request
       * @param commit Vuex commit
       * @param request Complaint data
       */
      createComplaint({ commit }, request: DoCreateComplaintFalconRequest) {
        FalconService.request<
          DoCreateComplaintFalconRequest,
          DoCreateComplaintFalconResponse
        >(RequestAlias.CreateComplaint, request).then(response => {
          commit(
            "complaintCreated",
            response.data.doCreateComplaintResponse.complaintId
          );
        });
      }
    },
    mutations: {
      /**
       * Updates complaints data
       * @param state Vuex state
       * @param complaintsData Complaints data
       */
      complaintsReceived(
        state,
        complaintsData: GetMyComplaintsComplaintListItem[]
      ) {
        state.complaintsData = complaintsData;
      },

      /**
       * Updates complaints total count
       * @param state Vuex state
       * @param complaintsCount Complaints total count
       */
      complaintsTotalCountReceived(state, complaintsCount: number) {
        state.complaintsTotalCount = complaintsCount;
      },

      /**
       * Updates complaint available quantity
       * @param state Vuex state
       * @param quantity Complaint available quantity
       */
      complaintAvailableQuantityReceived(state, quantity: number) {
        state.complaintQuantityAvailable = quantity;
      },

      /**
       * Updates created complaint id
       * @param state Vuex state
       * @param createdComplaintId Created complaint id
       */
      complaintCreated(state, createdComplaintId: string) {
        state.createdComplaintId = createdComplaintId;
      },

      /**
       * Updates first step form data
       * @param state Vuex state
       * @param formData First step form data
       */
      firstStepFormUpdated(
        state,
        formData: DoCreateComplaintFirstStepFormData
      ) {
        state.complaintFormData.details = { ...formData };
        if (state.complaintFormData.details.refuseAddCosts !== 0) {
          state.complaintFormData.details.compensationValue = undefined;
          state.complaintFormData.details.compensationCurrency = undefined;
        }
      },

      /**
       * Updates second step form data
       * @param state Vuex state
       * @param formData Second step form data
       */
      secondStepFormUpdated(
        state,
        formData: DoCreateComplaintSecondStepFormData
      ) {
        state.complaintFormData.vehicle = formData;
      },

      /**
       * Updates defect description present in third step form
       * @param state Vuex state
       * @param defectDescription Defect description
       */
      defectDescriptionUpdated(state, defectDescription: string) {
        state.complaintFormData.defectDescription = defectDescription;
      }
    }
  }
};
